import { useEffect, useRef } from "react";
import { usePostHog } from "posthog-js/react";
import { useUser } from "@clerk/nextjs";
import { UserProfile } from "./useUserProfile";

type ClerkUser = ReturnType<typeof useUser>['user'];

// Validation functions for secure PostHog integration
function validateEmail(email: string | undefined): string | undefined {
  if (!email || typeof email !== 'string') {
    return undefined;
  }
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email) ? email : undefined;
}

function getUserDisplayName(user: NonNullable<ClerkUser>): string {
  // Safe fallback chain with validation from Clerk user data
  if (user.fullName && typeof user.fullName === 'string' && user.fullName.trim()) {
    return user.fullName.trim();
  }
  
  const firstName = (user.firstName && typeof user.firstName === 'string') ? user.firstName.trim() : '';
  const lastName = (user.lastName && typeof user.lastName === 'string') ? user.lastName.trim() : '';
  const fullName = `${firstName} ${lastName}`.trim();
  
  if (fullName) {
    return fullName;
  }
  
  // Final fallback to email or "Unknown User"
  const validEmail = validateEmail(user.primaryEmailAddress?.emailAddress);
  return validEmail || 'Unknown User';
}

export function usePostHogIdentify(
  user: ClerkUser,
  profile: UserProfile | null
) {
  const posthog = usePostHog();
  const lastIdentifiedUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    let shouldCaptureSessionStart = false;
    
    // If no user, reset PostHog
    if (!user && posthog) {
      posthog.reset();
      lastIdentifiedUserIdRef.current = null;
      return;
    }
    
    // If no PostHog or no user, exit
    if (!posthog || !user) {
      return;
    }

    // Build user properties with secure validation
    const userProperties = {
      $email: validateEmail(user.primaryEmailAddress?.emailAddress),
      $name: getUserDisplayName(user),
      first_name: user.firstName,
      last_name: user.lastName,
      auth_provider: "clerk",
      email_verified: user.emailAddresses?.[0]?.verification?.status === "verified",
      created_at: user.createdAt ? new Date(user.createdAt).toISOString() : new Date().toISOString(),
      user_id: user.id,
    };

    // Remove null/undefined values
    const cleanProperties = Object.fromEntries(
      Object.entries(userProperties).filter(
        ([, value]) => value !== null && value !== undefined
      )
    );

    if (lastIdentifiedUserIdRef.current !== user.id) {
      // Set user properties with $set and $set_once
      const identifyPayload = {
        ...cleanProperties,
        $set: cleanProperties,
        $set_once: {
          first_seen: new Date().toISOString(),
          signup_date: user.createdAt ? new Date(user.createdAt).toISOString() : new Date().toISOString()
        }
      };
      
      if (process.env.NODE_ENV === 'development') {
        console.log('PostHog identifying user:', {
          userId: user.id,
          email: cleanProperties.$email,
          name: cleanProperties.$name,
          hasProfile: false,
          payload: identifyPayload
        });
      }
      
      // Always identify the user
      posthog.identify(user.id, identifyPayload);
      lastIdentifiedUserIdRef.current = user.id;
      shouldCaptureSessionStart = true;
    }

    // Update user properties even if already identified (in case profile was loaded after user)
    // User properties are already set above during identification

    if (shouldCaptureSessionStart) {
      posthog.capture("user_session_started", {
        auth_provider: "clerk",
        // Company information could be added from user metadata if needed
      });
    }

    return () => {
      // No external subscriptions to clean up; guard ref prevents repeated identify
    };
  }, [posthog, user]);

  // Update PostHog properties when Supabase profile loads
  useEffect(() => {
    if (!posthog || !user || !profile) return;

    const fullName = profile.first_name && profile.last_name
      ? `${profile.first_name} ${profile.last_name}`.trim()
      : profile.full_name || profile.first_name || profile.last_name;

    if (fullName) {
      if (process.env.NODE_ENV === 'development') {
        console.log('PostHog updating name from profile:', {
          userId: user.id,
          newName: fullName,
          firstName: profile.first_name,
          lastName: profile.last_name
        });
      }

      // Update user properties with real name from Supabase
      posthog.people.set({
        $name: fullName,
        first_name: profile.first_name,
        last_name: profile.last_name,
        full_name: profile.full_name,
        company_name: profile.company_name,
        phone: profile.phone
      });
    }
  }, [posthog, user, profile]);
}
